import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react';
import axios from 'axios';
import { API_URL } from '../config';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [currentOrg, setCurrentOrg] = useState(null);
  const [superAdminViewMode, setSuperAdminViewMode] = useState(
    localStorage.getItem('superAdminViewMode') || 'company'
  );

  // Configure axios defaults
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Fetch current user on mount
  const fetchUser = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/auth/me`);
      setUser(response.data);
      
      // Restore current org from localStorage
      const savedOrgId = localStorage.getItem('currentOrgId');
      if (savedOrgId) {
        try {
          const orgResponse = await axios.get(`${API_URL}/organizations/${savedOrgId}`);
          setCurrentOrg(orgResponse.data);
        } catch {
          localStorage.removeItem('currentOrgId');
        }
      }
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  useEffect(() => {
    if (!user) return;
    if (user.platform_role === 'super_admin') {
      const saved = localStorage.getItem('superAdminViewMode');
      const mode = saved === 'company' || saved === 'super_admin' ? saved : 'super_admin';
      setSuperAdminViewMode(mode);
      localStorage.setItem('superAdminViewMode', mode);
      return;
    }
    setSuperAdminViewMode('company');
    localStorage.removeItem('superAdminViewMode');
  }, [user]);

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data || {};

    if (!access_token || !userData) {
      // OTP flow (2FA or email verification) – no session token yet.
      return response.data;
    }
    
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    if (userData?.platform_role === 'super_admin') {
      const saved = localStorage.getItem('superAdminViewMode');
      const mode = saved === 'company' || saved === 'super_admin' ? saved : 'super_admin';
      setSuperAdminViewMode(mode);
      localStorage.setItem('superAdminViewMode', mode);
    } else {
      setSuperAdminViewMode('company');
      localStorage.removeItem('superAdminViewMode');
    }
    
    return userData;
  };

  const register = async (email, password, name) => {
    const response = await axios.post(`${API_URL}/auth/register`, { email, password, name });
    const { access_token, user: userData } = response.data || {};

    if (!access_token || !userData) {
      // Email verification required – no session token yet.
      return response.data;
    }
    
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    setSuperAdminViewMode('company');
    localStorage.removeItem('superAdminViewMode');
    
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('currentOrgId');
    localStorage.removeItem('superAdminViewMode');
    setToken(null);
    setUser(null);
    setCurrentOrg(null);
    setSuperAdminViewMode('company');
    delete axios.defaults.headers.common['Authorization'];
  };

  const selectOrganization = (org) => {
    setCurrentOrg(org);
    if (org) {
      localStorage.setItem('currentOrgId', org.id);
    } else {
      localStorage.removeItem('currentOrgId');
    }
  };

  const setSuperAdminMode = useCallback((mode) => {
    const nextMode = mode === 'super_admin' ? 'super_admin' : 'company';
    setSuperAdminViewMode(nextMode);
    if (user?.platform_role === 'super_admin') {
      localStorage.setItem('superAdminViewMode', nextMode);
    }
  }, [user?.platform_role]);

  /** Apply token + user after OTP verify (updates React state, not only localStorage). */
  const establishSession = useCallback((accessToken, userData) => {
    if (!accessToken || !userData) return;

    localStorage.setItem('token', accessToken);
    setToken(accessToken);
    setUser(userData);
    setLoading(false);
    axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;

    if (userData.platform_role === 'super_admin') {
      const saved = localStorage.getItem('superAdminViewMode');
      const mode = saved === 'company' || saved === 'super_admin' ? saved : 'super_admin';
      setSuperAdminViewMode(mode);
      localStorage.setItem('superAdminViewMode', mode);
    } else {
      setSuperAdminViewMode('company');
      localStorage.removeItem('superAdminViewMode');
    }
  }, []);

  const getPostLoginPath = useCallback((userData) => {
    if (userData?.platform_role === 'super_admin') {
      const mode = localStorage.getItem('superAdminViewMode');
      if (mode === 'super_admin') {
        return '/dashboard/super-admin/overview';
      }
    }
    return '/dashboard';
  }, []);

  const value = {
    user,
    token,
    loading,
    currentOrg,
    isAuthenticated: !!token && !!user,
    login,
    register,
    logout,
    selectOrganization,
    superAdminViewMode,
    setSuperAdminMode,
    establishSession,
    getPostLoginPath,
    refreshUser: fetchUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
